import { useMemo, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import { useLocalStorage } from "usehooks-ts";

import { SomethingWrong } from "components/common";
import LoadingScreen from "components/ui/LoadingScreen/LoadingScreen";

import { useEnvs } from "./EnvContext";
import { evalShouldFetchEnvs } from "./store";
import { useAuth } from "state/Auth/context";

// The purpose of EnvManager here is to manage the environment variables and fetch them from the server if they are not available in the local storage.
// Also, they handle cases where the user might manipulate the environment variables in the browser.
// The main store is `EnvStore` which is a zustand store that holds the environment variables and their version.
const EnvManager = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const hasFetchedBeforeRender = useRef(evalShouldFetchEnvs());

  const { isLoggedIn } = useAuth();

  const { fetchEnvs } = useEnvs();
  const [envs] = useLocalStorage("envs", {});
  const [version] = useLocalStorage("env_version", {});

  const isEnvEmpty = useMemo(
    () => isNaN(Number(version)) || !envs || Object.keys(envs).length === 0,
    [envs, version],
  );

  const isEnvsMissing = useMemo(() => {
    return !(envs as any).is_full_envs && isLoggedIn;
  }, [envs, isLoggedIn]);

  const { isLoading, isError, failureCount } = useQuery(
    ["get-environment-variables", isLoggedIn],
    () => {
      return fetchEnvs(isLoggedIn ? localStorage.getItem("token")! : undefined);
    },
    {
      enabled: isEnvEmpty || isEnvsMissing,
      retryDelay: 1000,
      refetchOnMount: false,
      onSuccess() {
        hasFetchedBeforeRender.current = false;
      },
      retry: (failureCount) => {
        if (failureCount > 3) {
          return false;
        }

        return true;
      },
      useErrorBoundary: false,
    },
  );

  const isEnvLoadedInState = useEnvs((state) => state.api);

  const isRenderBlocked =
    isLoading &&
    isEnvEmpty &&
    !hasFetchedBeforeRender.current &&
    !isEnvLoadedInState;

  if (isRenderBlocked) {
    return <LoadingScreen />;
  }

  if (isError && failureCount >= 2) {
    return <SomethingWrong />;
  }

  return <>{children}</>;
};

export { EnvManager };
